import { useCallback, useEffect, useState } from "react";
import { Radio, Download, Send, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";

const STEPS = [
  "Download the Live connector (once).",
  "In Rhino 7 or 8 run _RunPythonScript and pick RhinoDraft_Live.py.",
  "Hit “Send to Rhino” — the model builds itself and re-builds on every AI change or wall drag.",
];

export const RhinoLive = ({ project }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/rhino/status").then(({ data }) => setSt(data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const active = st?.active_project_id === project.project_id;
  const connector = async () => {
    setBusy("dl");
    try {
      await downloadFile(`/rhino/connector.py?base=${encodeURIComponent(process.env.REACT_APP_BACKEND_URL)}`, "RhinoDraft_Live.py");
      toast.success("Connector downloaded — run it in Rhino");
      setOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const send = async () => {
    setBusy("send");
    try {
      await api.post("/rhino/active", { project_id: project.project_id });
      await load();
      toast.success(st?.connected ? "Sent — building in Rhino now" : "Queued — it builds as soon as Rhino connects");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <div className="border border-[var(--line)] p-4 space-y-3" data-testid="rhino-live-panel">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold font-display"><Radio size={15} className="text-[var(--cyan)]" /> Rhino Live</span>
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest" data-testid="rhino-live-status">
          <span className={`w-2 h-2 rounded-full ${st?.connected ? "bg-[var(--emerald)] animate-pulse" : "bg-[var(--muted)]/50"}`} />
          {st?.connected ? "Rhino connected" : "Not connected"}
        </span>
      </div>
      <button data-testid="send-to-rhino-btn" onClick={send} disabled={!!busy}
        className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold bg-[var(--emerald)] text-[var(--bg)] hover:brightness-110 transition-[filter] disabled:opacity-50">
        {busy === "send" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        {active ? "Syncing this draft to Rhino" : "Send to Rhino"}
      </button>
      <button data-testid="download-connector-btn" onClick={connector} disabled={!!busy}
        className="w-full flex items-center justify-center gap-2 py-2 text-xs border border-[var(--line)] hover:border-[var(--cyan)] transition-colors disabled:opacity-50">
        {busy === "dl" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download Live connector (.py)
      </button>
      <button onClick={() => setOpen((o) => !o)} data-testid="rhino-live-help-toggle" className="flex items-center gap-1 text-[11px] text-[var(--muted)] hover:text-white transition-colors">
        How it works <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="space-y-1.5 text-[11px] text-[var(--muted)] leading-relaxed">
          {STEPS.map((s, i) => <li key={s} className="flex gap-2"><span className="font-mono text-[var(--cyan)]">{i + 1}</span>{s}</li>)}
          <li className="pt-1">Run the connector again to stop syncing. It keeps a private key — don’t share the file.</li>
        </ol>
      )}
    </div>
  );
};
